import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

export default function CreateMeeting() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [department, setDepartment] = useState("");
  const [customDept, setCustomDept] = useState("");
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) api.get("/departments").then((r) => setPresets(r.data.preset));
  }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    const dept = isAdmin
      ? (department === "__custom__" ? customDept.trim() : department)
      : user?.department;
    if (!dept) {
      toast.error("Please select or enter a department");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/meetings", { title, date, department: dept });
      toast.success("Meeting created");
      navigate(`/meetings/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-8 lg:p-12 max-w-2xl">
        <button
          onClick={() => navigate("/")}
          data-testid="back-btn"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-6"
        >
          <ArrowLeft size={16} /> Back to History
        </button>

        <div className="text-xs tracking-[0.2em] uppercase text-primary font-medium mb-2 mono">New Record</div>
        <h1 className="font-heading font-black text-4xl tracking-tight mb-8">Create Meeting</h1>

        <form onSubmit={submit} className="border border-border bg-card p-8 space-y-6">
          <div className="space-y-1.5">
            <Label htmlFor="title">Meeting Title</Label>
            <Input
              id="title"
              data-testid="meeting-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Q3 Budget Review"
              className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                data-testid="meeting-date-input"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="rounded-none mono focus-visible:ring-2 focus-visible:ring-primary"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              {isAdmin ? (
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger data-testid="meeting-department-select" className="rounded-none">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {presets.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                    <SelectItem value="__custom__">+ Custom department…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  data-testid="meeting-department-fixed"
                  value={user?.department || ""}
                  readOnly
                  disabled
                  className="rounded-none bg-muted"
                />
              )}
            </div>
          </div>

          {isAdmin && department === "__custom__" && (
            <div className="space-y-1.5">
              <Label htmlFor="custom">Custom Department Name</Label>
              <Input
                id="custom"
                data-testid="custom-department-input"
                value={customDept}
                onChange={(e) => setCustomDept(e.target.value)}
                placeholder="Research & Development"
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
          )}

          <div className="pt-2">
            <Button
              type="submit"
              disabled={loading}
              data-testid="create-meeting-submit"
              className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-8"
            >
              {loading ? "Creating…" : <><Check size={18} className="mr-2" /> Create Meeting</>}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
